import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Check, Circle, Clock, Pause, XCircle, Pencil, Undo2, Save,
  CalendarDays, Ban, FolderOpen, Mail, X, Send, FileText,
} from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";

const fmtDDMMYYYY = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d)) return "";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

// Parses "Documents collected · 3 of 12" into { stage, done, total }.
// Falls back to summary.done_count / total_count when the string is missing.
const parseProgressLabel = (summary) => {
  if (!summary) return { stage: "", done: 0, total: 0 };
  const label = summary.progress_label || "";
  const [stagePart, countPart] = label.split("·").map((s) => (s || "").trim());
  const m = (countPart || "").match(/(\d+)\s*of\s*(\d+)/i);
  const done = m ? Number(m[1]) : (summary.done_count || 0);
  const total = m ? Number(m[2]) : (summary.total_count || 0);
  return { stage: stagePart || "", done, total };
};

export const CaseStatusChip = ({ summary, compact = false, size = "md" }) => {
  if (!summary) return <span className="text-[11.5px] text-sage">—</span>;
  const { case_status } = summary;
  const { stage, done, total } = parseProgressLabel(summary);
  const complete = total > 0 && done >= total && case_status === "active";
  const isHold = case_status === "on_hold";
  const isCancelled = case_status === "cancelled";
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const barPct = isCancelled || isHold ? 100 : (complete ? 100 : pct);

  // Calm palette: green complete, gold in progress, grey waiting (pct=0),
  // amber on hold, red cancelled.
  let barColor = "bg-gold";
  let stateLabel = stage || "In progress";
  let stateCls = "text-forest";
  let subNote = `${done} of ${total}`;
  if (isCancelled) {
    barColor = "bg-red-400";
    stateLabel = "Cancelled";
    stateCls = "text-red-700";
    subNote = summary.case_cancel_reason || "Case closed";
  } else if (isHold) {
    barColor = "bg-amber-400";
    stateLabel = `${stage || "In progress"} · On hold`;
    stateCls = "text-amber-800";
    subNote = summary.case_hold_reason || `${done} of ${total}`;
  } else if (complete) {
    barColor = "bg-emerald-500";
    stateLabel = "Completed";
    stateCls = "text-emerald-700";
    subNote = `${total} of ${total}`;
  } else if (done === 0) {
    barColor = "bg-forest/15";
    stateCls = "text-forest/80";
  }

  const nameSz = size === "sm" || compact
    ? "text-[13px] leading-snug"
    : "text-[14.5px] leading-tight";
  const barH = size === "sm" || compact ? "h-2" : "h-2.5";

  return (
    <div className="space-y-1.5 w-full" data-testid="case-chip">
      <div className="flex items-center gap-2 flex-wrap">
        {isHold && <Pause size={13} strokeWidth={2} className="text-amber-700 shrink-0" />}
        {isCancelled && <XCircle size={13} strokeWidth={2} className="text-red-600 shrink-0" />}
        {complete && <Check size={14} strokeWidth={2.4} className="text-emerald-700 shrink-0" />}
        <p className={`font-semibold tracking-tight ${nameSz} ${stateCls} min-w-0 truncate`}>{stateLabel}</p>
        {!isCancelled && total > 0 && (
          <span className="text-[11.5px] text-forest/55 font-semibold whitespace-nowrap">
            {done} of {total}
          </span>
        )}
      </div>
      <div className={`${barH} w-full bg-cream-3 rounded-full overflow-hidden`}>
        <div
          className={`h-full ${barColor} rounded-full transition-[width] duration-500`}
          style={{ width: `${barPct}%` }}
        />
      </div>
      {(isHold || isCancelled) && subNote && (
        <p className="text-[11px] text-forest/55 truncate" title={subNote}>{subNote}</p>
      )}
    </div>
  );
};

// Compact label like "Application: Signed" / "Agreement: Draft" for the
// Status column. Hidden when the status is empty.
export const StatusLabel = ({ kind, status, testid }) => {
  if (!status) return null;
  const map = {
    application: {
      in_progress: { label: "In progress", cls: "bg-cream-3 text-forest/70 border-forest/15" },
      signed: { label: "Signed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    },
    agreement: {
      draft: { label: "Draft", cls: "bg-cream-3 text-forest/70 border-forest/15" },
      sent: { label: "Sent", cls: "bg-amber-50 text-amber-800 border-amber-200" },
      signed: { label: "Signed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    },
  };
  const row = map[kind] || {};
  const entry = row[status];
  if (!entry) return null;
  const prefix = kind === "application" ? "Application" : "Agreement";
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-[10.5px] font-bold tracking-[0.08em] px-2 py-0.5 border rounded ${entry.cls}`}
    >
      <span className="text-forest/55 font-semibold">{prefix}:</span> {entry.label}
    </span>
  );
};

const StepRow = ({ step, isCurrent, isLast, onToggle, onEdit, onClearNote, saving }) => {
  const { id, label, state, excluded, date_label } = step;
  const done = !!state?.done;
  const note = state?.note || "";
  const date = state?.date || null;
  const doneAt = state?.done_at;

  // Current step is visually bolder — slightly larger dot and ring.
  let dotCls =
    "rounded-full border flex items-center justify-center shrink-0 transition-all";
  if (excluded) dotCls += " w-9 h-9 bg-cream-3 text-forest/30 border-forest/15";
  else if (done) dotCls += " w-9 h-9 bg-emerald-500 text-white border-emerald-500";
  else if (isCurrent) dotCls += " w-11 h-11 bg-gold text-forest border-gold ring-4 ring-gold/25";
  else dotCls += " w-9 h-9 bg-white text-forest/40 border-forest/20";

  const labelCls = `${isCurrent && !done ? "text-[15px]" : "text-sm"} ${
    excluded
      ? "text-forest/40 line-through"
      : done
        ? "text-forest font-semibold"
        : isCurrent
          ? "text-forest font-bold"
          : "text-forest/60"
  }`;

  return (
    <div className="flex gap-3 sm:gap-4" data-testid={`case-step-${id}`}>
      <div className="flex flex-col items-center">
        <div className={dotCls}>
          {excluded ? <Ban size={14} strokeWidth={1.8} /> : done ? <Check size={16} strokeWidth={2.4} /> : isCurrent ? <Clock size={16} strokeWidth={2} /> : <Circle size={10} strokeWidth={2} />}
        </div>
        {!isLast && <div className={`flex-1 w-px ${done ? "bg-emerald-200" : "bg-forest/10"} min-h-[22px]`} />}
      </div>
      <div className="flex-1 pb-6 min-w-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className={labelCls}>
              {label}
              {excluded && <span className="ml-2 text-[10.5px] tracking-[0.14em] uppercase font-bold text-forest/40">Not needed</span>}
              {isCurrent && !done && !excluded && (
                <span className="ml-2 text-[10.5px] tracking-[0.14em] uppercase font-bold text-gold-dark">Current</span>
              )}
            </p>
            {done && doneAt && (
              <p className="text-[11px] text-sage mt-0.5">
                <span className="inline-flex items-center gap-1.5"><CalendarDays size={11} strokeWidth={1.7} /> Completed {fmtDDMMYYYY(doneAt)}</span>
              </p>
            )}
            {date && !excluded && (
              <p className="text-[11px] text-forest mt-0.5 font-semibold">
                {date_label || "Date"}: {fmtDDMMYYYY(date)}
              </p>
            )}
            {note && (
              <p className="text-[12px] text-ink/80 mt-1 bg-cream-3 border border-forest/10 rounded px-3 py-2 whitespace-pre-wrap max-w-xl">
                {note}
              </p>
            )}
          </div>
          <div className="flex gap-1 shrink-0">
            {!excluded && (
              <>
                <button
                  type="button"
                  onClick={() => onToggle(!done)}
                  disabled={saving}
                  data-testid={`case-step-${id}-toggle`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded text-[10.5px] font-bold tracking-[0.14em] uppercase transition-colors ${
                    done ? "bg-white text-forest/70 border-forest/20 hover:border-forest" : "bg-forest text-cream border-forest hover:bg-forest-soft"
                  }`}
                >
                  {done ? (<><Undo2 size={11} /> Undo</>) : (<><Check size={11} /> Done</>)}
                </button>
                <button
                  type="button"
                  onClick={onEdit}
                  disabled={saving}
                  data-testid={`case-step-${id}-edit`}
                  className="p-1.5 border border-forest/15 hover:border-forest rounded text-forest/70 hover:text-forest transition-colors"
                  title="Add note / date"
                >
                  <Pencil size={12} strokeWidth={1.8} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StepEditor = ({ step, onClose, onSave }) => {
  const [note, setNote] = useState(step?.state?.note || "");
  const [date, setDate] = useState(step?.state?.date || "");
  const [doneAt, setDoneAt] = useState((step?.state?.done_at || "").slice(0, 10));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        note: note.trim(),
        date: date || "",
        done_at: doneAt ? `${doneAt}T00:00:00Z` : undefined,
      });
      onClose();
    } catch (e) {
      // parent toasts
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-forest/15 rounded-xl max-w-md w-full p-6 space-y-4" data-testid="case-step-editor">
        <h3 className="font-serif text-lg text-forest">{step.label}</h3>
        {step.state?.done && (
          <div>
            <label className="block text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60 mb-1.5">Completion date</label>
            <input type="date" value={doneAt} onChange={(e) => setDoneAt(e.target.value)} data-testid="case-step-editor-done-at"
              className="w-full bg-cream-3 border border-forest/15 rounded px-3 py-2 text-sm focus:border-gold focus:outline-none" />
          </div>
        )}
        <div>
          <label className="block text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60 mb-1.5">{step.date_label || "Related date"} (optional)</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="case-step-editor-date"
            className="w-full bg-cream-3 border border-forest/15 rounded px-3 py-2 text-sm focus:border-gold focus:outline-none" />
        </div>
        <div>
          <label className="block text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60 mb-1.5">Note (optional)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="case-step-editor-note"
            placeholder="Short internal note…"
            className="w-full bg-cream-3 border border-forest/15 rounded px-3 py-2 text-sm focus:border-gold focus:outline-none" />
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-ghost-dark">Cancel</button>
          <button type="button" onClick={save} disabled={saving} data-testid="case-step-editor-save" className="btn-gold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Save size={13} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * ProgressTab — case progress tracker, admin-only. Mounted inside the customer
 * editor as the "Progress" tab. Mirrors a modern banking case view.
 */
export const ProgressTab = ({ customerId, customerName, onChanged }) => {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [reason, setReason] = useState("");
  const [chaseOpen, setChaseOpen] = useState(false);
  const [chaseNote, setChaseNote] = useState("");
  const [chasing, setChasing] = useState(false);
  const [comms, setComms] = useState([]);

  const loadComms = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/customers/${customerId}/communications`, authHeaders());
      setComms(data || []);
    } catch { /* non-critical */ }
  };

  const load = async () => {
    try {
      setLoadError("");
      const { data } = await axios.get(`${API}/admin/customers/${customerId}/case`, authHeaders());
      setData(data);
      setReason(data.case_status === "on_hold" ? (data.case_hold_reason || "") : data.case_status === "cancelled" ? (data.case_cancel_reason || "") : "");
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "The progress tracker didn't load.");
    }
  };

  useEffect(() => { load(); loadComms(); /* eslint-disable-next-line */ }, [customerId]);

  const updateCase = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/customers/${customerId}/case`, patch, authHeaders());
      setData(data);
      onChanged?.();
      toast.success("Updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    } finally {
      setSaving(false);
    }
  };

  const sendChase = async () => {
    setChasing(true);
    try {
      const { data: result } = await axios.post(
        `${API}/admin/customers/${customerId}/documents/email-chase`,
        { note: chaseNote },
        authHeaders(),
      );
      toast.success(`Sent — ${result.count} ${result.count === 1 ? "item" : "items"} on the chase list`);
      setChaseOpen(false);
      setChaseNote("");
      // Refresh so the "Last chased on" caption updates immediately.
      await load();
      await loadComms();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send email");
    } finally {
      setChasing(false);
    }
  };

  const updateStep = async (stepId, patch) => {    setSaving(true);
    try {
      // When marking done without an explicit done_at, use today.
      if (patch.done === true && !patch.done_at) patch.done_at = `${todayISO()}T00:00:00Z`;
      const { data } = await axios.put(
        `${API}/admin/customers/${customerId}/case/steps/${stepId}`,
        patch,
        authHeaders(),
      );
      setData(data);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update step");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded">
        {loadError} <button onClick={load} className="underline ml-2">Retry</button>
      </div>
    );
  }
  if (!data) return <p className="text-sm text-sage">Loading…</p>;

  const { case_type, case_status, case_valuation_not_needed, steps, done_count, total_count, current_step_id } = data;
  const pct = total_count > 0 ? Math.round((done_count / total_count) * 100) : 0;

  const barCls = case_status === "cancelled"
    ? "bg-red-400"
    : case_status === "on_hold"
    ? "bg-amber-400"
    : done_count >= total_count && total_count > 0
    ? "bg-emerald-500"
    : "bg-gold";

  // Current + next step look-ups for the "Next step: …" line.
  const currentStep = steps.find((s) => s.id === current_step_id);
  const activeSteps = steps.filter((s) => !s.excluded);
  const nextIncomplete = activeSteps.find((s) => !s.state?.done);
  const isComplete = done_count >= total_count && total_count > 0;

  return (
    <div className="space-y-6" data-testid="case-progress-tab">
      {/* Header: type + status */}
      <div className="bg-white border border-forest/10 rounded-xl p-5 sm:p-6 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-gold font-bold">Case tracker</p>
            <h3 className="font-serif text-xl text-forest mt-1">{customerName}</h3>
            <div className="flex items-center gap-2 flex-wrap mt-1.5">
              <p className="text-[12px] text-sage">{data.progress_label || `${done_count}/${total_count}`}</p>
              {data.docs && (
                <span
                  data-testid="case-docs-chip"
                  title={`${data.docs.received} of ${data.docs.applicable} documents collected`}
                  className={`inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.08em] px-2 py-0.5 border rounded-sm ${
                    data.docs.applicable > 0 && data.docs.received >= data.docs.applicable
                      ? "bg-forest text-cream border-forest"
                      : data.docs.received > 0
                        ? "bg-gold-soft/30 text-gold-dark border-gold/40"
                        : "bg-cream-3 text-forest/60 border-forest/15"
                  }`}
                >
                  <FolderOpen size={11} strokeWidth={1.8} />
                  {data.docs.received}/{data.docs.applicable} documents
                </span>
              )}
              {data.docs && data.docs.applicable > data.docs.received && (
                <button
                  type="button"
                  data-testid="case-docs-chase-button"
                  onClick={() => setChaseOpen(true)}
                  className="inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 border border-gold text-gold-dark hover:bg-gold hover:text-forest transition-colors rounded-sm"
                  title={data.email ? `Email ${data.email} the outstanding list` : "No email on file"}
                >
                  <Mail size={11} strokeWidth={1.8} /> Chase docs
                </button>
              )}
              {data.last_doc_chase_at && (
                <span className="text-[11px] text-sage" title={`${data.last_doc_chase_count || 0} items on the last email`}>
                  · last chased {fmtDDMMYYYY(data.last_doc_chase_at)}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {["active", "on_hold", "cancelled"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => updateCase({ case_status: s })}
                disabled={saving}
                data-testid={`case-status-${s}`}
                className={`px-3 py-1.5 border rounded text-[10.5px] font-bold tracking-[0.14em] uppercase transition-colors ${
                  case_status === s
                    ? s === "cancelled"
                      ? "bg-red-600 text-white border-red-600"
                      : s === "on_hold"
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-forest text-cream border-forest"
                    : "bg-white text-forest/70 border-forest/15 hover:border-forest"
                }`}
              >
                {s === "active" ? "Active" : s === "on_hold" ? "On hold" : "Cancelled"}
              </button>
            ))}
          </div>
        </div>

        {/* Case type */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60">Case type</span>
          <div className="flex gap-2">
            {[
              { v: "mortgage", l: "Mortgage (purchase)" },
              { v: "remortgage", l: "Remortgage" },
            ].map((t) => (
              <button
                key={t.v}
                type="button"
                onClick={() => updateCase({ case_type: t.v })}
                disabled={saving}
                data-testid={`case-type-${t.v}`}
                className={`px-3 py-1.5 border rounded text-[11px] font-bold tracking-[0.1em] uppercase transition-colors ${
                  case_type === t.v ? "bg-gold text-forest border-gold" : "bg-white text-forest/70 border-forest/15 hover:border-forest"
                }`}
              >{t.l}</button>
            ))}
          </div>
          {case_type === "remortgage" && (
            <label className="inline-flex items-center gap-2 text-[11px] text-forest/70 ml-2">
              <input
                type="checkbox"
                checked={!!case_valuation_not_needed}
                onChange={(e) => updateCase({ case_valuation_not_needed: e.target.checked })}
                data-testid="case-valuation-not-needed"
                className="accent-gold"
              />
              Valuation not needed
            </label>
          )}
        </div>

        {/* Reason for hold / cancelled */}
        {(case_status === "on_hold" || case_status === "cancelled") && (
          <div>
            <label className="block text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60 mb-1.5">
              {case_status === "on_hold" ? "Why is this on hold?" : "Why was this cancelled?"}
            </label>
            <div className="flex gap-2">
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => updateCase(case_status === "on_hold" ? { case_hold_reason: reason } : { case_cancel_reason: reason })}
                data-testid="case-reason-input"
                maxLength={400}
                className="flex-1 bg-cream-3 border border-forest/15 rounded px-3 py-2 text-sm focus:border-gold focus:outline-none"
                placeholder="Short internal note…"
              />
            </div>
          </div>
        )}

        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between text-[12px] text-forest/70 mb-2">
            <span className="font-bold tracking-[0.14em] uppercase text-forest/60">Progress</span>
            <span className="font-semibold">{done_count} of {total_count}{total_count > 0 ? ` · ${pct}%` : ""}</span>
          </div>
          <div className="h-3 bg-cream-3 rounded-full overflow-hidden">
            <div
              className={`h-full ${barCls} rounded-full transition-[width] duration-500 flex items-center justify-end pr-1.5`}
              style={{ width: `${isComplete || case_status === "cancelled" || case_status === "on_hold" ? 100 : pct}%` }}
            >
              {isComplete && <Check size={12} strokeWidth={3} className="text-white" />}
            </div>
          </div>
          {case_status === "active" && (
            <p className="mt-2.5 text-[13px] text-forest/80" data-testid="case-next-step-line">
              {isComplete ? (
                <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-700">
                  <Check size={13} strokeWidth={2.4} /> All steps complete — case ready to close.
                </span>
              ) : (
                <>
                  <span className="font-bold tracking-[0.14em] uppercase text-[10.5px] text-gold-dark mr-2">Next step</span>
                  <span className="font-semibold text-forest">{(nextIncomplete || currentStep)?.label || "—"}</span>
                </>
              )}
            </p>
          )}
          {case_status === "on_hold" && (
            <p className="mt-2.5 text-[13px] text-amber-800" data-testid="case-next-step-line">
              <span className="font-bold tracking-[0.14em] uppercase text-[10.5px] mr-2">On hold</span>
              <span className="font-semibold">{data.case_hold_reason || "Progress paused."}</span>
            </p>
          )}
          {case_status === "cancelled" && (
            <p className="mt-2.5 text-[13px] text-red-700" data-testid="case-next-step-line">
              <span className="font-bold tracking-[0.14em] uppercase text-[10.5px] mr-2">Cancelled</span>
              <span className="font-semibold">{data.case_cancel_reason || "Case closed."}</span>
            </p>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white border border-forest/10 rounded-xl p-5 sm:p-6">
        <h4 className="font-serif text-lg text-forest mb-5">Timeline</h4>
        <div data-testid="case-timeline">
          {steps.map((step, i) => (
            <StepRow
              key={step.id}
              step={step}
              isCurrent={step.id === current_step_id}
              isLast={i === steps.length - 1}
              onToggle={(done) => updateStep(step.id, { done })}
              onEdit={() => setEditingStep(step)}
              saving={saving}
            />
          ))}
        </div>
      </div>

      {editingStep && (
        <StepEditor
          step={editingStep}
          onClose={() => setEditingStep(null)}
          onSave={(payload) => updateStep(editingStep.id, payload)}
        />
      )}

      {/* Communications trail — every email sent to this customer */}
      <div className="bg-white border border-forest/10 rounded-xl p-5 sm:p-6" data-testid="case-comms-trail">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-gold font-bold">Communications</p>
            <h3 className="font-serif text-lg text-forest mt-0.5">Emails sent to this customer</h3>
          </div>
          {comms.length > 0 && (
            <p className="text-[11px] text-sage">{comms.length} {comms.length === 1 ? "entry" : "entries"}</p>
          )}
        </div>
        {comms.length === 0 ? (
          <p className="text-[13px] text-sage">No emails have been sent to this customer yet from the back office.</p>
        ) : (
          <ul className="divide-y divide-forest/10">
            {comms.map((c) => (
              <li key={c.id} className="py-3 flex items-start gap-3" data-testid={`case-comms-row-${c.kind}`}>
                <div className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 ${
                  c.kind === "invoice_sent"
                    ? "bg-forest text-cream border-forest"
                    : "bg-gold-soft/30 text-gold-dark border-gold/40"
                }`}>
                  {c.kind === "invoice_sent" ? <FileText size={13} /> : <Mail size={13} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[13px] font-semibold text-forest truncate">{c.subject}</p>
                    <span className={`text-[9.5px] tracking-[0.18em] uppercase font-bold px-1.5 py-0.5 border rounded-sm ${
                      c.kind === "chase_all" ? "border-gold/50 text-gold-dark bg-gold-soft/20"
                      : c.kind === "chase_item" ? "border-gold/40 text-gold-dark bg-white"
                      : c.kind === "invoice_sent" ? "border-forest/40 text-forest bg-cream-3"
                      : "border-forest/30 text-forest/70 bg-cream-3"
                    }`}>
                      {c.kind === "chase_all" ? "Full chase" : c.kind === "chase_item" ? "Single item" : c.kind === "invoice_sent" ? "Invoice" : c.kind}
                    </span>
                  </div>
                  <p className="text-[11.5px] text-sage mt-0.5">
                    To <span className="text-forest/80">{c.to}</span>
                    {c.item && <> · <span className="text-forest/80">{c.item}</span></>}
                    {" · "}{fmtDDMMYYYY(c.created_at)}
                  </p>
                  {c.note && (
                    <p className="text-[12px] text-ink/80 italic mt-1">"{c.note}"</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {chaseOpen && (
        <div className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm flex items-center justify-center p-5">
          <div
            data-testid="case-docs-chase-modal"
            className="bg-white border border-forest/15 rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.2)] max-w-lg w-full p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] tracking-[0.24em] uppercase text-gold font-bold">Chase documents</p>
                <h3 className="font-serif text-lg text-forest mt-1">Email the outstanding list</h3>
              </div>
              <button type="button" onClick={() => setChaseOpen(false)} className="p-1 text-forest/60 hover:text-forest"><X size={18} /></button>
            </div>
            {data.email ? (
              <>
                <p className="text-[13px] text-sage">
                  We'll email <span className="font-semibold text-forest">{data.email}</span> a tidy list of the <span className="font-semibold text-forest">{data.docs.applicable - data.docs.received}</span> {data.docs.applicable - data.docs.received === 1 ? "item" : "items"} still outstanding.
                </p>
                <div>
                  <label className="block text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60 mb-1.5">Add a note (optional)</label>
                  <textarea
                    rows={3}
                    value={chaseNote}
                    onChange={(e) => setChaseNote(e.target.value)}
                    maxLength={600}
                    data-testid="case-docs-chase-note"
                    placeholder="e.g. Thanks for your call this morning — here's the list we agreed."
                    className="w-full bg-cream-3 border border-forest/15 rounded-lg px-3 py-2.5 text-sm focus:border-gold focus:outline-none"
                  />
                </div>
                <div className="flex gap-2 justify-end pt-1">
                  <button type="button" onClick={() => setChaseOpen(false)} className="btn-ghost-dark">Cancel</button>
                  <button
                    type="button"
                    onClick={sendChase}
                    disabled={chasing}
                    data-testid="case-docs-chase-send"
                    className="btn-gold disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    <Send size={14} /> {chasing ? "Sending…" : "Send email"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-[13px] text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
                  This customer has no email address on file. Add one on the Details tab first.
                </p>
                <div className="flex justify-end">
                  <button type="button" onClick={() => setChaseOpen(false)} className="btn-ghost-dark">Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
